import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import SEO, { serviceSchema } from '../../components/SEO';
import {
  ArrowRight,
  Sparkles,
  Award,
  Building2,
  Users,
  Heart,
  Presentation,
  PartyPopper,
  Handshake,
  GraduationCap,
  Stethoscope,
  Send,
  MapPin,
  Calendar,
  Clock,
  Mail,
  QrCode,
  BarChart3,
  CheckCircle2,
  Sparkle,
  Gift,
  Search,
  Fingerprint,
  Lock,
  Eye,
  Download,
  Share2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { phoneMockups, heroMosaic } from '../../mock';
import { getLangFromPath, localizePath } from '../../i18n';

/* -------- Phone frame -------- */
function PhoneFrame({ children }) {
  return (
    <div className="relative mx-auto w-[220px] md:w-[240px]">
      <div className="relative aspect-[9/19] rounded-[2.5rem] bg-gradient-to-b from-[#1a1a24] to-[#0a0a0f] p-2 border border-white/10 shadow-2xl shadow-black/70">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-b-2xl z-30" />
        <div className="relative w-full h-full rounded-[2rem] overflow-hidden bg-[#0a0a0f]">
          {children}
        </div>
      </div>
      <div className="absolute -inset-6 -z-10 bg-violet-500/12 blur-3xl rounded-full" />
    </div>
  );
}

/* Screen 1: Corporate photo search */
function ScreenEntry() {
  const { t } = useTranslation();
  return (
    <>
      <img src={phoneMockups.personB} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/60" />

      {/* Event header */}
      <div className="absolute top-10 left-3 right-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[10px] font-black shadow">
          B26
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold text-white leading-tight">{t('mockups.corporate.eventTitle')}</div>
          <div className="text-[8px] text-white/70 leading-tight">{t('mockups.corporate.eventLoc')}</div>
        </div>
        <div className="text-[8px] font-medium text-emerald-300">{t('mockups.corporate.photosCount')}</div>
      </div>

      {/* Face detection frame */}
      <div className="absolute top-[34%] left-1/2 -translate-x-1/2 w-[52%] aspect-square">
        <span className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-white rounded-tl-md" />
        <span className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-white rounded-tr-md" />
        <span className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-white rounded-bl-md" />
        <span className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-white rounded-br-md" />
      </div>

      {/* Bottom info */}
      <div className="absolute bottom-6 left-3 right-3 text-center">
        <div className="text-[10px] font-semibold text-white mb-1">{t('mockups.corporate.lookingForPhotos')}</div>
        <div className="text-[8px] text-white/70 mb-2">{t('mockups.corporate.selfieHint')}</div>
        <div className="w-11 h-11 rounded-full bg-white mx-auto" />
      </div>
    </>
  );
}

/* Screen 2: Album with sponsors */
function ScreenAlbumSponsors() {
  const { t } = useTranslation();
  return (
    <>
      <div className="absolute inset-0 bg-[#0a0a0f]" />

      {/* Header with main sponsor */}
      <div className="absolute top-10 left-0 right-0 px-3">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[9px] font-black">
            B26
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold text-white leading-tight">{t('mockups.corporate.albumTitle')}</div>
            <div className="text-[8px] text-white/50 leading-tight">{t('mockups.corporate.userLabel')}</div>
          </div>
        </div>

        {/* Main sponsor banner */}
        <div className="rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 p-1.5 flex items-center gap-1.5">
          <div className="w-5 h-5 rounded bg-white flex items-center justify-center text-[7px] font-black text-emerald-600">M</div>
          <div className="flex-1 text-[7px] text-white leading-tight">
            <div className="font-bold">{t('mockups.corporate.sponsorTag')}</div>
            <div className="opacity-80">{t('mockups.corporate.sponsorTier')}</div>
          </div>
          <div className="text-[7px] text-white/80">→</div>
        </div>
      </div>

      {/* Featured photo */}
      <div className="absolute top-[32%] left-3 right-3 rounded-lg overflow-hidden aspect-[4/3] border border-white/8">
        <img src={phoneMockups.personA} alt="" className="w-full h-full object-cover" />
      </div>

      {/* Album grid */}
      <div className="absolute top-[58%] left-3 right-3">
        <div className="grid grid-cols-4 gap-1 mb-2">
          {[phoneMockups.album1, phoneMockups.album3, phoneMockups.album4, phoneMockups.album5].map((s, i) => (
            <div key={i} className="aspect-square rounded overflow-hidden">
              <img src={s} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>

        {/* Secondary sponsors row */}
        <div className="text-[7px] uppercase tracking-wider text-white/40 mb-1">{t('mockups.corporate.sponsorsRow')}</div>
        <div className="flex items-center gap-1">
          {[
            { name: 'Santander', bg: 'bg-red-600' },
            { name: 'YPF', bg: 'bg-blue-500' },
            { name: 'Coca', bg: 'bg-red-500' },
            { name: 'Ford', bg: 'bg-blue-700' },
          ].map((s) => (
            <div key={s.name} className={`flex-1 h-6 rounded ${s.bg} flex items-center justify-center`}>
              <span className="text-[7px] font-bold text-white">{s.name}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* Screen 3: Follow-up email */
function ScreenEmail() {
  const { t } = useTranslation();
  return (
    <>
      <div className="absolute inset-0 bg-white" />

      {/* Email header */}
      <div className="absolute top-8 left-0 right-0 px-3 pb-2 border-b border-black/8">
        <div className="text-[9px] text-black/50 mb-1">{t('mockups.corporate.emailFrom')}</div>
        <div className="text-[9px] text-black/50 mb-2">{t('mockups.corporate.emailTo')}</div>
        <div className="text-[13px] font-black text-black leading-tight">{t('mockups.corporate.emailTitleGift')}</div>
      </div>

      {/* Email body */}
      <div className="absolute top-[26%] left-3 right-3">
        <div className="text-[8px] text-black leading-relaxed mb-2">
          {t('mockups.corporate.emailBody')}
        </div>

        {/* Hero photo */}
        <div className="w-full aspect-[16/10] rounded-lg overflow-hidden mb-2">
          <img src={phoneMockups.personC} alt="" className="w-full h-full object-cover" />
        </div>

        {/* Buttons */}
        <div className="flex gap-1 mb-2">
          <div className="flex-1 h-6 rounded bg-blue-600 flex items-center justify-center text-[8px] font-bold text-white">{t('mockups.common.viewMyAlbum')}</div>
          <div className="w-6 h-6 rounded bg-black/10 flex items-center justify-center text-black">
            <Download className="w-3 h-3" />
          </div>
        </div>

        {/* Sponsor CTA */}
        <div className="rounded-lg border border-black/8 p-2 bg-gradient-to-br from-emerald-50 to-teal-50">
          <div className="text-[7px] uppercase tracking-wider text-emerald-700 font-semibold mb-0.5">{t('mockups.corporate.sponsorGiftEyebrow')}</div>
          <div className="text-[8px] text-black font-semibold leading-tight">{t('mockups.corporate.sponsorGiftBody')}</div>
          <div className="text-[7px] text-black/60 mt-0.5">{t('mockups.corporate.couponCode')}</div>
        </div>
      </div>
    </>
  );
}

/* Sponsor tiers visual */
function SponsorTiers() {
  const { t } = useTranslation();
  const tiers = [
    { name: t('mockups.corporate.tierPlatinum'), badge: 'from-slate-100 to-slate-300 text-slate-900', logos: ['Movistar'], size: 'h-16' },
    { name: t('mockups.corporate.tierGold'), badge: 'from-amber-300 to-amber-500 text-amber-900', logos: ['Santander', 'YPF'], size: 'h-12' },
    { name: t('mockups.corporate.tierSilver'), badge: 'from-zinc-300 to-zinc-400 text-zinc-900', logos: ['Coca-Cola', 'Ford', 'Aerolíneas'], size: 'h-10' },
    { name: t('mockups.corporate.tierBronze'), badge: 'from-amber-700 to-amber-900 text-amber-50', logos: ['Belgrano', 'Ualá', 'Rappi', 'Naranja X'], size: 'h-8' },
  ];
  return (
    <div className="space-y-4">
      {tiers.map((t) => (
        <div key={t.name} className="flex items-center gap-4">
          <div className="w-20 shrink-0 text-right">
            <div className={`inline-block px-3 py-1 rounded-full bg-gradient-to-r ${t.badge} text-[10px] font-black uppercase tracking-wider`}>{t.name}</div>
          </div>
          <div className="flex-1 flex flex-wrap gap-2">
            {t.logos.map((l) => (
              <div key={l} className={`${t.size} px-4 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center min-w-[100px]`}>
                <span className="text-white/85 font-display font-bold text-sm">{l}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* Timeline visual */
function TouchpointsTimeline() {
  const steps = [
    { icon: QrCode, label: 'Difusión del QR', desc: 'En el venue, invitaciones y pantallas', tone: 'text-cyan-300 bg-cyan-500/15 border-cyan-400/30' },
    { icon: Sparkles, label: 'En vivo', desc: 'Fotos publicadas en tiempo real', tone: 'text-violet-300 bg-violet-500/15 border-violet-400/30' },
    { icon: Mail, label: 'Email post-evento', desc: 'Álbum + cupón / agradecimiento', tone: 'text-emerald-300 bg-emerald-500/15 border-emerald-400/30' },
    { icon: BarChart3, label: 'Follow-up', desc: 'Reporte de engagement y sponsors', tone: 'text-fuchsia-300 bg-fuchsia-500/15 border-fuchsia-400/30' },
  ];
  return (
    <div className="relative">
      <div className="absolute top-8 left-8 right-8 h-px bg-gradient-to-r from-blue-400 via-violet-400 to-fuchsia-400 opacity-40" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 relative">
        {steps.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="flex flex-col items-center text-center">
              <div className={`w-16 h-16 rounded-2xl border ${s.tone} flex items-center justify-center relative z-10`}>
                <Icon className="w-7 h-7" />
              </div>
              <div className="mt-3 text-sm font-semibold text-white">{s.label}</div>
              <div className="text-xs text-white/50 mt-1 max-w-[130px]">{s.desc}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Email templates mockup */
function EmailTemplatesMockup() {
  const { t } = useTranslation();
  return (
    <div className="relative">
      {/* Card 1 - Cupón */}
      <div className="rounded-2xl bg-white shadow-2xl p-4 max-w-[300px] relative z-10">
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-black/8">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-black text-[10px]">B26</div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold text-black leading-tight">{t('mockups.corporate.thanksTitle')}</div>
            <div className="text-[8px] text-black/50">{t('mockups.corporate.emailTo')}</div>
          </div>
        </div>
        <div className="text-[11px] font-black text-black leading-tight mb-2">{t('mockups.corporate.thanksBody')}</div>
        <div className="w-full aspect-[16/10] rounded-lg overflow-hidden mb-2">
          <img src={phoneMockups.personA} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 p-2.5 text-white">
          <div className="text-[8px] uppercase tracking-wider opacity-80">{t('mockups.common.exclusiveCoupon')}</div>
          <div className="text-sm font-black leading-tight">{t('mockups.corporate.exclusiveCouponBody')}</div>
          <div className="text-[9px] mt-1 opacity-90">{t('mockups.corporate.couponHint')}</div>
        </div>
      </div>

      {/* Card 2 - Agradecimiento */}
      <div className="rounded-2xl bg-white shadow-2xl p-4 max-w-[280px] absolute top-16 right-0 rotate-3 z-20">
        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-black/8">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white">
            <Heart className="w-3.5 h-3.5" fill="currentColor" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold text-black leading-tight">{t('mockups.corporate.galaTitle')}</div>
            <div className="text-[8px] text-black/50">{t('mockups.corporate.galaTo')}</div>
          </div>
        </div>
        <div className="text-[11px] font-black text-black leading-tight mb-2">{t('mockups.corporate.galaSubject')}</div>
        <div className="text-[9px] text-black/70 leading-relaxed mb-2">
          {t('mockups.corporate.galaBodyPrefix')} <span className="font-bold text-rose-600">$4.2M</span> {t('mockups.corporate.galaBodySuffix')}
        </div>
        <div className="grid grid-cols-3 gap-1 mb-2">
          {[phoneMockups.album1, phoneMockups.album2, phoneMockups.album3].map((s, i) => (
            <div key={i} className="aspect-square rounded overflow-hidden">
              <img src={s} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
        <div className="h-6 rounded bg-rose-500 flex items-center justify-center text-white text-[9px] font-bold">
          {t('mockups.common.viewMyAlbum')}
        </div>
      </div>

      {/* Card 3 - Invitación próximo evento */}
      <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-2xl p-4 max-w-[260px] absolute bottom-0 left-8 -rotate-2 z-0">
        <div className="text-[9px] uppercase tracking-wider opacity-80 mb-1">{t('mockups.corporate.nextEventEyebrow')}</div>
        <div className="text-sm font-black leading-tight mb-2">{t('mockups.corporate.nextYearBody')}</div>
        <div className="text-[9px] opacity-90 leading-relaxed mb-3">
          {t('mockups.corporate.nextYearDesc')}
        </div>
        <div className="h-6 rounded bg-white text-blue-700 flex items-center justify-center text-[9px] font-bold">
          {t('mockups.corporate.reserveSpot')}
        </div>
      </div>
    </div>
  );
}


/* Privacy modes visual — phone mockup with face-detected album */
function PrivacyModesMockup() {
  const { t } = useTranslation();
  const tiles = [
    { src: heroMosaic.left[0].src, mine: false },
    { src: heroMosaic.left[1].src, mine: true },
    { src: heroMosaic.left[2].src, mine: false },
    { src: heroMosaic.right[0].src, mine: false },
    { src: heroMosaic.left[3].src, mine: true },
    { src: heroMosaic.right[2].src, mine: false },
    { src: heroMosaic.right[3].src, mine: false },
    { src: phoneMockups.personA, mine: true },
    { src: phoneMockups.album2, mine: false },
  ];

  return (
    <div className="relative mx-auto w-[290px] md:w-[320px]">
      <div className="relative aspect-[9/19] rounded-[2.5rem] bg-gradient-to-b from-[#1a1a24] to-[#0a0a0f] p-2 border border-white/10 shadow-2xl shadow-black/70">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-b-2xl z-30" />
        <div className="relative w-full h-full rounded-[2rem] overflow-hidden bg-[#0a0a0f]">
          {/* Status bar */}
          <div className="absolute top-0 left-0 right-0 z-20 px-6 pt-3 flex items-center justify-between text-[10px] text-white/85 font-medium">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <div className="w-3 h-1.5 rounded-sm bg-white/80" />
              <div className="w-4 h-2 rounded-sm border border-white/60" />
            </div>
          </div>

          {/* Brand header */}
          <div className="absolute top-10 left-0 right-0 px-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-[9px] font-black text-white">CO</div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-semibold text-white leading-tight">{t('mockups.corporate.privacyEventTitle')}</div>
              <div className="text-[9px] text-white/50 leading-tight">{t('mockups.corporate.privacyAlbumMeta')}</div>
            </div>
            <div className="w-6 h-6 rounded-md bg-white/10 flex items-center justify-center">
              <Search className="w-3 h-3 text-white/70" />
            </div>
          </div>

          {/* Privacy toggle */}
          <div className="absolute top-24 left-3 right-3 rounded-xl bg-white/[0.04] border border-white/10 p-1.5 flex">
            <div className="flex-1 py-1.5 rounded-lg text-center text-[10px] text-white/50 font-medium">
              {t('mockups.corporate.privacyOpen')}
            </div>
            <div className="flex-1 py-1.5 rounded-lg text-center text-[10px] font-semibold bg-gradient-to-r from-violet-500/80 to-fuchsia-500/80 text-white shadow-lg shadow-violet-500/25 flex items-center justify-center gap-1">
              <Lock className="w-2.5 h-2.5" /> {t('mockups.corporate.privacyClosed')}
            </div>
          </div>

          {/* Album grid with face detection */}
          <div className="absolute top-[26%] left-3 right-3 grid grid-cols-3 gap-1.5">
            {tiles.map((tile, i) => (
              <div key={i} className={`aspect-square rounded-md overflow-hidden relative border ${tile.mine ? 'border-violet-400/60 ring-1 ring-violet-400/50' : 'border-white/8 opacity-40 blur-[1px]'}`}>
                <img src={tile.src} alt="" className="w-full h-full object-cover" />
                {tile.mine && (
                  <div className="absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-violet-500 flex items-center justify-center">
                    <CheckCircle2 className="w-2.5 h-2.5 text-white" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer chip */}
          <div className="absolute bottom-6 left-3 right-3 flex items-center gap-2 rounded-xl bg-violet-500/10 border border-violet-400/25 px-3 py-2">
            <Fingerprint className="w-4 h-4 text-violet-300 shrink-0" />
            <div className="text-[10px] text-white/80 leading-tight">
              {t('mockups.corporate.privacyFootline')}
              <span className="block text-violet-200/70 text-[9px]">{t('mockups.corporate.verifiedBySelfie')}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute -inset-6 -z-10 bg-violet-500/15 blur-3xl rounded-full" />
    </div>
  );
}

/* Photo wall mockup */
function PhotoWallMockup() {
  const { t } = useTranslation();
  return (
    <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#141425] to-[#0e0e14] p-6 h-[380px]">
      <div className="absolute inset-0 grid grid-cols-8 gap-1 opacity-40">
        {Array.from({ length: 32 }).map((_, i) => {
          const src = [
            phoneMockups.album1, phoneMockups.album2, phoneMockups.album3, phoneMockups.album4,
            phoneMockups.album5, phoneMockups.album6, phoneMockups.personA, phoneMockups.personB,
            heroMosaic.left[0].src, heroMosaic.left[1].src, heroMosaic.left[2].src, heroMosaic.left[3].src,
            heroMosaic.right[0].src, heroMosaic.right[1].src, heroMosaic.right[2].src, heroMosaic.right[3].src,
          ][i % 16];
          return (
            <div key={i} className="aspect-square rounded overflow-hidden">
              <img src={src} alt="" className="w-full h-full object-cover" />
            </div>
          );
        })}
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-transparent to-[#0a0a0f]/50" />
      <div className="relative h-full flex flex-col items-center justify-center text-center">
        <div className="w-24 h-24 rounded-2xl bg-white p-3 mb-4 shadow-2xl">
          <div className="grid grid-cols-6 gap-[1px] w-full h-full">
            {Array.from({ length: 36 }).map((_, i) => {
              const pattern = [0, 1, 2, 3, 5, 7, 8, 9, 11, 12, 14, 15, 18, 19, 20, 22, 25, 27, 28, 30, 31, 33, 34, 35];
              return <div key={i} className={pattern.includes(i) ? 'bg-black rounded-[1px]' : 'bg-white'} />;
            })}
          </div>
        </div>
        <div className="font-display text-2xl md:text-3xl font-bold text-white">{t('mockups.corporate.scanMe')}</div>
        <div className="text-sm text-white/70 mt-2">{t('mockups.corporate.scanSub')}</div>
      </div>
    </div>
  );
}

const useCases = [
  {
    icon: Presentation,
    title: 'Convención anual de ventas',
    desc: 'Que cada vendedor se lleve un recuerdo de la premiación. Sponsors patrocinadores presentes en cada álbum.',
    tag: 'B2B',
    gradient: 'from-blue-500/15 to-cyan-500/10 border-blue-400/25',
  },
  {
    icon: Stethoscope,
    title: 'Congreso médico',
    desc: 'Speakers, stands y networking. Cada asistente recibe sus fotos con el patrocinio de labs sponsors.',
    tag: 'Salud',
    gradient: 'from-emerald-500/15 to-teal-500/10 border-emerald-400/25',
  },
  {
    icon: PartyPopper,
    title: 'Fiesta de fin de año',
    desc: 'Colaboradores, familias y momentos. Álbum interno con la marca de la empresa y ganas de compartir.',
    tag: 'Interno',
    gradient: 'from-fuchsia-500/15 to-pink-500/10 border-fuchsia-400/25',
  },
  {
    icon: Award,
    title: 'Premios y galas benéficas',
    desc: 'Combinás sponsors, medios y invitados VIP. Cada ganador se lleva sus fotos y comparten en redes.',
    tag: 'Prestige',
    gradient: 'from-amber-500/15 to-orange-500/10 border-amber-400/25',
  },
  {
    icon: Handshake,
    title: 'Kick-off / off-site',
    desc: 'Team building con memoria. Fotos + comentarios + tag automático del equipo con branding interno.',
    tag: 'Cultura',
    gradient: 'from-violet-500/15 to-purple-500/10 border-violet-400/25',
  },
  {
    icon: GraduationCap,
    title: 'Graduaciones corporativas',
    desc: 'MBA, programas ejecutivos, in-company. Ceremonia con foto individual + branded diploma preview.',
    tag: 'Educación',
    gradient: 'from-rose-500/15 to-red-500/10 border-rose-400/25',
  },
];

/* Auto-cycling carousel between LATAM Fintech Market and Movistar Experience */
const CORP_MOCKUP_SETS = [
  {
    label: 'LATAM Fintech Market 2026',
    images: [
      'https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/oz1yna22_MOCKUP%20EVENTO%20TECH%20%281%29.webp',
      'https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/9skfjbdw_MOCKUP%20EVENTO%20TECH%20%282%29.webp',
      'https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/l9l0g2tb_MOCKUP%20EVENTO%20TECH%20%283%29.webp',
    ],
    glow: 'rgba(139,92,246,0.28)',
  },
  {
    label: 'Movistar · Experience',
    images: [
      'https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/h98nf5t7_MOCKUP%20APP%20WEB%20MOMENTIA-07.png',
      'https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/lner6df8_MOCKUP%20APP%20WEB%20MOMENTIA-08.png',
      'https://customer-assets-cm19k8pv.emergentagent.net/job_vende-fotos-pro/artifacts/6ua7w01r_MOCKUP%20APP%20WEB%20MOMENTIA-09.png',
    ],
    glow: 'rgba(0,168,255,0.32)',
  },
];

function CorporateMockupsCarousel() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((v) => (v + 1) % CORP_MOCKUP_SETS.length), 4000);
    return () => clearInterval(t);
  }, []);

  const current = CORP_MOCKUP_SETS[idx];

  return (
    <div className="relative">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-6">
        {current.images.map((src, i) => (
          <div
            key={`${idx}-${i}`}
            className="relative flex justify-center brands-mockup-fade"
            style={{ animationDelay: `${i * 0.08}s` }}
          >
            <img
              src={src}
              alt={`${current.label} mockup ${i + 1}`}
              className="w-full max-w-[280px] h-auto object-contain"
              style={{ filter: `drop-shadow(0 25px 50px ${current.glow})` }}
              loading="lazy"
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-center gap-4 mt-8">
        <div className="text-xs uppercase tracking-[0.25em] text-white/60 font-medium">
          {current.label}
        </div>
        <div className="flex gap-2">
          {CORP_MOCKUP_SETS.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Ver set ${i + 1}`}
              className={`w-8 h-1 rounded-full transition-all ${
                i === idx ? 'bg-white' : 'bg-white/20 hover:bg-white/40'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Corporate() {
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const to = (p) => localizePath(p, lang);
  const howSteps = t('services.corporate.howItWorks.steps', { returnObjects: true }) || [];
  const sponsorItems = t('services.corporate.sponsors.items', { returnObjects: true }) || [];
  const emailItems = t('services.corporate.emails.items', { returnObjects: true }) || [];
  const privacyModes = t('services.corporate.privacy.modes', { returnObjects: true }) || [];
  const ideas = t('services.corporate.ideas.items', { returnObjects: true }) || [];
  const measureTags = t('services.corporate.measure.tags', { returnObjects: true }) || [];
  return (
    <>
      <SEO
        title={t('services.corporate.seoTitle')}
        description={t('services.corporate.seoDescription')}
        path="/servicios/eventos"
        schema={serviceSchema({ name: 'Fotos con IA para Eventos Corporativos', description: 'Plataforma de fotos con reconocimiento facial para convenciones, kickoffs, galas empresariales y summits.', path: '/servicios/eventos' })}
      />
      {/* HERO */}
      <section className="relative overflow-hidden pt-16 lg:pt-20 pb-16">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 -left-32 w-[500px] h-[500px] bg-blue-500/12 rounded-full blur-[140px]" />
          <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-violet-500/12 rounded-full blur-[160px]" />
        </div>
        <div className="container-mi relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Link to={to('/')} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-violet-300 mb-5 hover:text-violet-200 transition-colors">
                <span>{t('services.corporate.breadcrumb')}</span><span>/</span><span className="text-white/60">{t('services.corporate.breadcrumbCurrent')}</span>
              </Link>
              <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-[1.02]">
                {t('services.corporate.heroTitle1')}
                <span className="block bg-gradient-to-r from-blue-300 via-violet-300 to-fuchsia-300 bg-clip-text text-transparent">
                  {t('services.corporate.heroTitleHighlight')}
                </span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
                {t('services.corporate.heroSubtitle')}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to={to('/request-demo')}>
                  <Button className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-6 rounded-full font-semibold">
                    {t('services.corporate.ctaDemo')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to={to('/precios')}>
                  <Button variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                    {t('services.corporate.ctaPricing')}
                  </Button>
                </Link>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
                <div>
                  <div className="font-display text-2xl font-bold text-white">97%</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.corporate.heroStats.activation')}</div>
                </div>
                <div>
                  <div className="font-display text-2xl font-bold text-white">3.4x</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.corporate.heroStats.sponsorReturn')}</div>
                </div>
                <div>
                  <div className="font-display text-2xl font-bold text-white">12s</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wider">{t('services.corporate.heroStats.timeInAlbum')}</div>
                </div>
              </div>
            </div>

            {/* Right: photo wall */}
            <div className="lg:col-span-5">
              <PhotoWallMockup />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS - carousel between LFM Tech and Movistar Experience */}
      <section className="py-20 lg:py-24 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3">{t('services.corporate.howItWorks.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('services.corporate.howItWorks.title')}
            </h2>
            <p className="mt-4 text-lg text-white/60">{t('services.corporate.howItWorks.subtitle')}</p>
          </div>

          <CorporateMockupsCarousel />

          <div className="grid md:grid-cols-3 gap-10 mt-10 max-w-5xl mx-auto">
            {[
              { n: 1, color: 'bg-blue-500' },
              { n: 2, color: 'bg-violet-500' },
              { n: 3, color: 'bg-emerald-500' },
            ].map((s, i) => (
              <div key={s.n} className="flex flex-col items-center text-center">
                <div className={`w-8 h-8 rounded-full ${s.color} flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
                  {s.n}
                </div>
                <h3 className="font-display text-lg md:text-xl font-semibold mt-3 mb-2">{howSteps[i]?.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed max-w-xs">{howSteps[i]?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPONSOR ECOSYSTEM */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3 flex items-center gap-2">
                <Award className="w-3.5 h-3.5" /> {t('services.corporate.sponsors.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                {t('services.corporate.sponsors.titleLine1')} <span className="bg-gradient-to-r from-blue-300 to-fuchsia-300 bg-clip-text text-transparent">{t('services.corporate.sponsors.titleHighlight')}</span>
                <br />
                {t('services.corporate.sponsors.titleLine2')}
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                {t('services.corporate.sponsors.description')}
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  { icon: Sparkles, i: 0 },
                  { icon: Gift, i: 1 },
                  { icon: QrCode, i: 2 },
                  { icon: BarChart3, i: 3 },
                ].map(({ icon: Icon, i }) => (
                  <li key={i} className="flex items-start gap-3 text-white/80">
                    <div className="w-8 h-8 rounded-lg bg-violet-500/10 border border-violet-400/25 flex items-center justify-center text-violet-300 shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-sm leading-relaxed pt-1">{sponsorItems[i]}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl bg-[#08080c] border border-white/8 p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="text-sm font-semibold text-white">{t('services.corporate.sponsors.dashboardTitle')}</div>
                <div className="text-[10px] text-white/40 uppercase tracking-wider">{t('services.corporate.sponsors.dashboardActive')}</div>
              </div>
              <SponsorTiers />
              <div className="mt-6 pt-6 border-t border-white/8 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="font-display text-xl font-bold text-white">380k</div>
                  <div className="text-[9px] text-white/50 uppercase tracking-wider">{t('services.corporate.sponsors.dashboardImpressions')}</div>
                </div>
                <div>
                  <div className="font-display text-xl font-bold text-white">14.2k</div>
                  <div className="text-[9px] text-white/50 uppercase tracking-wider">{t('services.corporate.sponsors.dashboardClicks')}</div>
                </div>
                <div>
                  <div className="font-display text-xl font-bold text-emerald-300">3.4x ROI</div>
                  <div className="text-[9px] text-white/50 uppercase tracking-wider">{t('services.corporate.sponsors.dashboardReturn')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TOUCHPOINTS TIMELINE */}
      <section className="py-24 lg:py-28 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div className="max-w-3xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3">{t('services.corporate.touchpoints.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('services.corporate.touchpoints.titleLine1')}
              <br />
              {t('services.corporate.touchpoints.titleLine2')}
            </h2>
            <p className="mt-4 text-lg text-white/60">
              {t('services.corporate.touchpoints.subtitle')}
            </p>
          </div>
          <TouchpointsTimeline />
        </div>
      </section>

      {/* USE CASES CREATIVE */}
      <section className="py-24 lg:py-28">
        <div className="container-mi">
          <div className="max-w-3xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3">{t('services.corporate.useCasesSection.eyebrow')}</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
              {t('services.corporate.useCasesSection.titleLine1')}
              <br />
              {t('services.corporate.useCasesSection.titleLine2')}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {useCases.map((u) => {
              const Icon = u.icon;
              return (
                <div key={u.title} className={`card-lift rounded-2xl p-6 bg-gradient-to-br ${u.gradient} border`}>
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center text-white">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">{u.tag}</div>
                  </div>
                  <h3 className="font-display text-lg font-bold mb-2 text-white">{u.title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">{u.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CUSTOMIZED EMAILS */}
      <section className="py-24 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 -translate-y-1/2 -left-40 w-[500px] h-[500px] bg-emerald-500/8 rounded-full blur-[140px]" />
          <div className="absolute top-1/2 -translate-y-1/2 -right-40 w-[500px] h-[500px] bg-rose-500/8 rounded-full blur-[140px]" />
        </div>
        <div className="container-mi relative">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3 flex items-center gap-2">
                <Mail className="w-3.5 h-3.5" /> {t('services.corporate.emails.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                {t('services.corporate.emails.titleLine1')}
                <br />
                {t('services.corporate.emails.titleLine2Prefix')} <span className="bg-gradient-to-r from-emerald-300 to-rose-300 bg-clip-text text-transparent">{t('services.corporate.emails.titleHighlight')}</span>
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                {t('services.corporate.emails.description')}
              </p>

              <div className="mt-8 space-y-3">
                {[Gift, Heart, Calendar, BarChart3, Sparkles].map((Icon, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-[#111116] border border-white/8">
                    <div className="w-9 h-9 rounded-lg bg-violet-500/10 border border-violet-400/25 flex items-center justify-center text-violet-300 shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white">{emailItems[i]?.title}</div>
                      <div className="text-xs text-white/60 mt-0.5">{emailItems[i]?.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative h-[520px]">
              <EmailTemplatesMockup />
            </div>
          </div>
        </div>
      </section>

      {/* PRIVACY MODES */}
      <section className="py-24 lg:py-28 bg-[#08080c] border-y border-white/5">
        <div className="container-mi">
          <div>
            <div className="max-w-3xl">
              <div className="text-xs uppercase tracking-[0.2em] text-violet-300 mb-3 flex items-center gap-2">
                <Lock className="w-3.5 h-3.5" /> {t('services.corporate.privacy.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                {t('services.corporate.privacy.titleLine1')}
                <br />
                <span className="bg-gradient-to-r from-emerald-300 to-violet-300 bg-clip-text text-transparent">{t('services.corporate.privacy.titleHighlight')}</span>
              </h2>
              <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                {t('services.corporate.privacy.description')}
              </p>
            </div>
            <div className="mt-10 grid md:grid-cols-3 gap-4">
                <div className="flex items-start gap-3 p-4 rounded-xl bg-[#111116] border border-emerald-400/20">
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center text-emerald-300 shrink-0">
                    <Eye className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{privacyModes[0]?.title}</div>
                    <div className="text-xs text-white/60 mt-1">{privacyModes[0]?.desc}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-[#111116] border border-violet-400/20">
                  <div className="w-9 h-9 rounded-lg bg-violet-500/15 border border-violet-400/30 flex items-center justify-center text-violet-300 shrink-0">
                    <Fingerprint className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{privacyModes[1]?.title}</div>
                    <div className="text-xs text-white/60 mt-1">{privacyModes[1]?.desc}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-gradient-to-br from-violet-500/10 to-fuchsia-500/5 border border-violet-400/25">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500/30 to-fuchsia-500/30 border border-violet-400/40 flex items-center justify-center text-violet-200 shrink-0">
                    <Users className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">{privacyModes[2]?.title}</div>
                    <div className="text-xs text-white/60 mt-1">{privacyModes[2]?.desc}</div>
                  </div>
                </div>
              </div>
          </div>
        </div>
      </section>

      {/* CREATIVE IDEAS */}
      <section className="py-24 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/15 via-violet-500/10 to-fuchsia-500/15 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-fuchsia-500/15 rounded-full blur-[160px]" />
        <div className="container-mi relative">
          <div className="max-w-3xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-white/70 mb-3 flex items-center gap-2">
              <Sparkle className="w-3.5 h-3.5" /> {t('services.corporate.ideas.eyebrow')}
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight text-white">
              {t('services.corporate.ideas.title')}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[Gift, Send, MapPin, Clock, Heart, Building2, Share2, QrCode].map((Icon, i) => (
              <div key={i} className="rounded-2xl bg-white/[0.06] backdrop-blur-md border border-white/15 p-5 hover:bg-white/[0.09] transition-colors">
                <div className="w-10 h-10 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center text-white mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="font-display font-semibold text-white text-base mb-1.5">{ideas[i]?.title}</div>
                <div className="text-sm text-white/70 leading-relaxed">{ideas[i]?.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEASURE SUCCESS + SPONSOR STATS */}
      <section className="py-24 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-500/20 via-fuchsia-500/12 to-amber-500/20 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-violet-500/20 rounded-full blur-[160px]" />
        <div className="container-mi relative">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/70 mb-3 flex items-center gap-2">
                <BarChart3 className="w-3.5 h-3.5" /> {t('services.corporate.measure.eyebrow')}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight text-white">
                {t('services.corporate.measure.titleLine1')}
                <br />
                {t('services.corporate.measure.titleLine2')}
              </h2>
              <p className="mt-5 text-lg text-white/85 leading-relaxed max-w-xl">
                {t('services.corporate.measure.description')}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {measureTags.map((tag) => (
                  <span key={tag} className="px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-sm text-white">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Getpica-style dashboard mockup */}
            <div className="relative w-full">
              <div className="rounded-2xl bg-white text-black p-4 shadow-2xl">
                {/* Fake browser bar */}
                <div className="flex items-center gap-1 mb-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  <div className="ml-3 text-[9px] text-black/50">dashboard.momentia.shop / SAP Now BsAs</div>
                </div>

                {/* Top KPIs */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="font-display font-bold text-black">{t('mockups.corporate.statsTitle')}</div>
                  <div className="ml-auto text-[9px] text-black/50">{t('mockups.corporate.statsPeriod')}</div>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="p-2 rounded-lg border border-black/8">
                    <div className="text-[9px] text-pink-600 font-semibold">{t('mockups.corporate.checkins')}</div>
                    <div className="text-lg font-display font-bold text-black">4.284</div>
                    <div className="text-[9px] text-black/50">{t('mockups.corporate.statsAccept')}</div>
                  </div>
                  <div className="p-2 rounded-lg border border-black/8">
                    <div className="text-[9px] text-violet-600 font-semibold">{t('mockups.corporate.photosAvg')}</div>
                    <div className="text-lg font-display font-bold text-black">16</div>
                    <div className="text-[9px] text-black/50">{t('mockups.corporate.delivered68k')}</div>
                  </div>
                  <div className="p-2 rounded-lg border border-black/8">
                    <div className="text-[9px] text-fuchsia-600 font-semibold">{t('mockups.corporate.engagement')}</div>
                    <div className="text-lg font-display font-bold text-black">31%</div>
                    <div className="text-[9px] text-black/50">{t('mockups.corporate.engagementSub')}</div>
                  </div>
                </div>

                {/* Chart */}
                <div className="p-3 rounded-lg border border-black/8 mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-semibold text-black">{t('mockups.corporate.checkinsByDay')}</div>
                    <div className="flex items-center gap-1">
                      <div className="px-1.5 py-0.5 rounded bg-violet-500 text-white text-[8px] font-semibold">{t('mockups.corporate.tabDay')}</div>
                      <div className="px-1.5 py-0.5 rounded text-black/50 text-[8px]">{t('mockups.corporate.tabWeek')}</div>
                      <div className="px-1.5 py-0.5 rounded text-black/50 text-[8px]">{t('mockups.corporate.tabMonth')}</div>
                    </div>
                  </div>
                  <div className="flex items-end justify-between gap-1 h-16">
                    {[42, 78, 55, 68, 92, 82, 74, 88, 96, 70, 58, 84, 68].map((h, i) => (
                      <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-violet-400 via-fuchsia-500 to-amber-400" style={{ height: `${h}%` }} />
                    ))}
                  </div>
                </div>

                {/* Sponsor stats row */}
                <div className="p-3 rounded-lg border border-black/8 bg-gradient-to-br from-amber-50 to-orange-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-semibold text-black flex items-center gap-1">
                      <Award className="w-3 h-3 text-amber-600" /> {t('mockups.corporate.sponsorReport')}
                    </div>
                    <div className="text-[9px] text-black/50">{t('mockups.corporate.sponsorsList')}</div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <div className="text-[9px] text-black/50">{t('mockups.corporate.impressions')}</div>
                      <div className="text-sm font-display font-bold text-black">3.2M</div>
                    </div>
                    <div>
                      <div className="text-[9px] text-black/50">🔗 Clicks a links</div>
                      <div className="text-sm font-display font-bold text-black">142k</div>
                    </div>
                    <div>
                      <div className="text-[9px] text-black/50">🎟️ Cupones</div>
                      <div className="text-sm font-display font-bold text-black">1.842</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating badges */}
              <div className="absolute -top-3 -left-3 px-2 py-1 rounded-full bg-white border border-black/8 text-[10px] font-semibold text-black shadow-lg flex items-center gap-1">
                😊 <span>+12 check-ins</span>
              </div>
              <div className="absolute top-1/2 -right-4 px-2 py-1 rounded-full bg-white border border-black/8 text-[10px] font-semibold text-black shadow-lg flex items-center gap-1">
                🔥 <span>+141 shares</span>
              </div>
              <div className="absolute -bottom-3 left-8 px-2 py-1 rounded-full bg-white border border-black/8 text-[10px] font-semibold text-black shadow-lg flex items-center gap-1">
                🎟️ <span>+38 cupones</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container-mi">
          <div className="relative overflow-hidden rounded-3xl border border-violet-400/20 bg-gradient-to-br from-[#141425] via-[#101018] to-[#0e0e14] p-10 md:p-14 text-center">
            <div className="absolute -top-32 -right-24 w-[500px] h-[500px] bg-violet-500/15 rounded-full blur-[100px]" />
            <div className="relative">
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
                {t('services.corporate.finalCta.title')}
              </h2>
              <p className="mt-4 text-white/70 max-w-xl mx-auto">
                {t('services.corporate.finalCta.subtitle')}
              </p>
              <div className="mt-8 flex justify-center gap-3 flex-wrap">
                <Link to={to('/request-demo')}>
                  <Button className="bg-white text-[#0a0a0f] hover:bg-white/90 h-12 px-7 rounded-full font-semibold">
                    {t('services.corporate.finalCta.ctaDemo')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to={to('/precios')}>
                  <Button variant="outline" className="h-12 px-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-white">
                    {t('services.corporate.finalCta.ctaPricing')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
